<script lang="ts">
  import type { Snippet } from 'svelte';
  import { tick } from 'svelte';
  import InlineMarkdown from './InlineMarkdown.svelte';
  import KeyboardHints, { type KeyHint } from './KeyboardHints.svelte';
  import VerdictBadge from './VerdictBadge.svelte';
  import { hasCommandModifier, isEditableTarget, isSubmitChord } from './keyboard';
  import {
    CARD_TYPE_LABELS,
    TEXTBOOK_ORIGIN,
    type DrillCard,
    type GradeResult,
    type GradeState,
  } from './types';

  interface Props {
    card: DrillCard;
    /** 1 始まり */
    index: number;
    total: number;
    state: GradeState;
    result?: GradeResult | undefined;
    /** Leitner の箱。画面には出さない（problem-first.md 追記。呼び出し側の互換のために受け取るだけ） */
    box?: number;
    /** 問い（<h1>） */
    prompt: string;
    /** 新しいカードの最初の 1 回だけ出す */
    showUseCase?: boolean;
    /** 採点キーの表示（1 行入力・選択肢は Enter、複数行は ⌘ Enter） */
    submitKeys?: string[];
    /** 採点ボタンを押せるか */
    canSubmit?: boolean;
    children: Snippet;
    actions?: Snippet;
    /** 判定欄に足すカード固有の表示（差分など） */
    feedback?: Snippet;
    /** 入力欄の外で押された文字キーをカードに渡す。処理したら true */
    onshortcut?: ((e: KeyboardEvent) => boolean) | undefined;
    onsubmit?: (() => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onhint?: ((level: number) => void) | undefined;
    onabort?: (() => void) | undefined;
    /**
     * true: フォーカスがカードの外（本文など）にあってもキーを受ける（1 画面 1 カードのドリル・カード画面）。
     * false（既定）: カードの内側で起きたキーだけを受ける（レッスンのように複数のカードが並ぶ画面）。
     * どちらでも、別の CardShell の内側で起きたキーは無視する。
     */
    globalKeys?: boolean;
  }

  // box は呼び出し側の互換のために受け取るだけで描かない
  // eslint-disable-next-line svelte/no-unused-props
  let {
    card,
    index,
    total,
    state: gradeState,
    result,
    prompt,
    showUseCase = false,
    submitKeys = ['Enter'],
    canSubmit = true,
    children,
    actions,
    feedback,
    onshortcut,
    onsubmit,
    onnext,
    onprev,
    onhint,
    onabort,
    globalKeys = false,
  }: Props = $props();

  const uid = $props.id();
  const titleId = `${uid}-q`;
  const hintsId = `${uid}-hints`;

  const hints = $derived(card.hints ?? []);
  let hintsOpen = $state(0);
  let hintsVisible = $state(true);
  let article: HTMLElement | undefined = $state();
  let nextButton: HTMLButtonElement | undefined = $state();

  const graded = $derived(gradeState === 'graded' && result !== undefined);
  const isLast = $derived(index >= total);
  const textbookUrl = $derived(`${TEXTBOOK_ORIGIN}${card.textbook}`);

  // カードが替わったらヒントを閉じる
  let lastCardKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key !== lastCardKey) {
      lastCardKey = key;
      hintsOpen = 0;
      hintsVisible = true;
    }
  });

  // 採点したらフォーカスを「次へ」へ移す
  let wasGraded = false;
  $effect(() => {
    const now = graded;
    if (now && !wasGraded) {
      void tick().then(() => nextButton?.focus());
    }
    wasGraded = now;
  });

  function openHint() {
    if (hintsOpen >= hints.length) {
      hintsVisible = true;
      return;
    }
    hintsOpen += 1;
    hintsVisible = true;
    onhint?.(hintsOpen);
  }

  function toggleHints() {
    if (hintsOpen === 0 || !hintsVisible || hintsOpen < hints.length) openHint();
    else hintsVisible = false;
  }

  function submit() {
    if (graded || gradeState === 'grading' || !canSubmit) return;
    onsubmit?.();
  }

  function focusTitle() {
    article?.querySelector<HTMLElement>('h1')?.focus();
  }

  /** このカードが受けるキーか（components.md §1.1 キーの届く範囲） */
  function isMine(e: KeyboardEvent): boolean {
    if (!article) return false;
    const target = e.target instanceof Node ? e.target : null;
    if (target && article.contains(target)) return true;
    if (!globalKeys) return false;
    // カードの外。別のカードの内側なら、そのカードに任せる
    const el = target instanceof Element ? target : (target?.parentElement ?? null);
    return el?.closest('[data-card-shell]') == null;
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.defaultPrevented || e.isComposing) return;
    if (!isMine(e)) return;
    const editing = isEditableTarget(e.target);

    if (e.key === 'Escape') {
      e.preventDefault();
      if (editing) {
        // 1 回目はフォーカスをカードに戻すだけ
        if (e.target instanceof HTMLElement) e.target.blur();
        focusTitle();
      } else if (hintsOpen > 0 && hintsVisible) {
        hintsVisible = false;
      } else {
        onabort?.();
      }
      return;
    }

    if (isSubmitChord(e)) {
      e.preventDefault();
      if (graded) onnext?.();
      else submit();
      return;
    }

    if (hasCommandModifier(e) || editing) return;

    const target = e.target instanceof Element ? e.target : null;
    const onControl = target?.closest('button, a, summary, [role="button"], [role="tab"]') != null;

    if (e.key === 'Enter') {
      if (onControl) return; // ボタン・リンク自身の Enter を優先する
      e.preventDefault();
      if (graded) onnext?.();
      else submit();
      return;
    }
    if (e.key === 'ArrowRight' && graded) {
      e.preventDefault();
      onnext?.();
      return;
    }
    if (e.key === 'ArrowLeft' && index > 1) {
      e.preventDefault();
      onprev?.();
      return;
    }
    if (e.key === '?' && hints.length > 0) {
      e.preventDefault();
      openHint();
      return;
    }
    if (onshortcut?.(e)) e.preventDefault();
  }

  const keyHints = $derived<KeyHint[]>([
    ...(graded
      ? [{ keys: ['→'], label: isLast ? '結果へ' : '次へ' }]
      : [{ keys: submitKeys, label: '採点' }]),
    ...(index > 1 ? [{ keys: ['←'], label: '前のカード' }] : []),
    ...(hints.length > 0 ? [{ keys: ['?'], label: 'ヒント' }] : []),
    { keys: ['Esc'], label: '中断' },
  ]);
</script>

<svelte:window {onkeydown} />

<article
  bind:this={article}
  class="card"
  class:graded-ac={graded && result?.verdict === 'AC'}
  class:graded-other={graded && result?.verdict !== 'AC'}
  aria-labelledby={titleId}
  data-testid="card"
  data-card-shell
  data-card-id={card.id}
  data-state={gradeState}
>
  <div class="card-meta">
    <span class="kind">{CARD_TYPE_LABELS[card.type]}</span>
    <span>Stage {card.stage}</span>
    <span class="spacer"></span>
    {#if hints.length > 0}
      <button
        class="btn btn-quiet btn-sm"
        type="button"
        aria-expanded={hintsOpen > 0 && hintsVisible}
        aria-controls={hintsId}
        data-testid="hint"
        onclick={toggleHints}
      >
        ヒント{#if hintsOpen > 0}<span class="num">&nbsp;{hintsOpen} / {hints.length}</span>{/if}
        <kbd>?</kbd>
      </button>
    {/if}
  </div>

  {#if showUseCase && card.useCase}
    <p class="use-case"><span class="eyebrow">こんなとき</span> {card.useCase}</p>
  {/if}

  <h1 id={titleId} tabindex="-1"><InlineMarkdown text={prompt} /></h1>

  {@render children()}

  <div id={hintsId} class="hints" hidden={hintsOpen === 0 || !hintsVisible}>
    {#each hints.slice(0, hintsOpen) as hint, i (i)}
      <section class="hint" aria-label={`ヒント ${i + 1} / ${hints.length}`}>
        <h2>ヒント {i + 1} / {hints.length}</h2>
        <p><InlineMarkdown text={hint} /></p>
      </section>
    {/each}
  </div>

  {#if !graded}
    <div class="actions">
      <button
        class="btn btn-primary"
        type="button"
        data-testid="submit"
        disabled={gradeState === 'grading' || !canSubmit}
        aria-busy={gradeState === 'grading' ? 'true' : undefined}
        onclick={submit}
      >
        {gradeState === 'grading' ? '判定中' : '採点する'}
        {#each submitKeys as k (k)}<kbd>{k}</kbd>{/each}
      </button>
      {#if actions}{@render actions()}{/if}
      <span class="spacer"></span>
      <a href={textbookUrl} target="_blank" rel="noopener"
        >教科書で読む<span class="visually-hidden">（新しいタブ）</span> ↗</a
      >
    </div>
  {/if}

  <div
    class="feedback"
    class:empty={!graded}
    role="status"
    aria-live="polite"
    data-testid="feedback"
  >
    {#if graded && result}
      <VerdictBadge verdict={result.verdict} />
      {#if result.message}<p>{result.message}</p>{/if}
      {#if feedback}{@render feedback()}{/if}
      <div class="explanation"><InlineMarkdown text={card.explanation} block /></div>
      <div class="actions">
        <button
          bind:this={nextButton}
          class="btn btn-primary"
          type="button"
          data-testid="next"
          onclick={() => onnext?.()}
        >
          {isLast ? '結果を見る' : '次へ'} <kbd>→</kbd>
        </button>
        <span class="spacer"></span>
        <a href={textbookUrl} target="_blank" rel="noopener" data-testid="textbook-link"
          >教科書で読む<span class="visually-hidden">（新しいタブ）</span> ↗</a
        >
      </div>
    {/if}
  </div>
</article>

<KeyboardHints hints={keyHints} />

<style>
  .card {
    background: var(--surface);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-card);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    border: 2px solid transparent;
    transition: border-color var(--dur-fast) var(--ease-out);
    min-width: 0;
  }
  /* 正解: 枠が AC の色に変わる。不正解は揺らさず、静かな輪郭だけ */
  .card.graded-ac {
    border-color: var(--verdict-ac);
  }
  .card.graded-other {
    border-color: var(--border-strong);
  }
  .card-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .card-meta .kind {
    color: var(--accent-soft-text);
    background: var(--accent-soft);
    padding: 0 var(--space-2);
    border-radius: var(--radius-xs);
    font-weight: var(--fw-semibold);
  }
  .spacer {
    flex: 1;
  }
  .use-case {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .use-case .eyebrow {
    margin-right: var(--space-2);
  }
  h1 {
    font-size: var(--fs-lg);
    line-height: var(--lh-ui);
  }
  .hints {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .hint {
    border-left: 3px solid var(--info-text);
    background: var(--info-bg);
    color: var(--text);
    padding: var(--space-3) var(--space-4);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  }
  .hint h2 {
    font-size: var(--fs-sm);
    color: var(--info-text);
    margin-bottom: var(--space-1);
  }
  .hint :global(a) {
    color: var(--link);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .feedback {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding-top: var(--space-4);
    border-top: 1px solid var(--border);
  }
  /* 判定前も live region を DOM に残す（出た瞬間に読み上げさせるため）。見た目だけ消す */
  .feedback.empty {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    border: 0;
  }
  .explanation {
    color: var(--text);
  }
  @media (max-width: 639px) {
    .card {
      padding: var(--space-4);
    }
  }
</style>
